import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  Collapse,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import image1 from '../../assets/riyal.png';
import { useTranslation } from '../../hooks/useTranslation';


const CreditNoteSummary = ({
  items = [],
  additionalCost = 0,
  discountPercentage = 0,
  discountAmount = 0,
  referenceInvoice = { amount: 0 },
  calculateSubtotal,
  calculateVat,
  calculateTotal,
  getTotalPayments,
  getRemainingBalance,
  formatCurrency = (val) => (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <img src={image1} alt="SAR" style={{ width: '13px', height: '13px', marginRight: '4px' }} />
      {parseFloat(val || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
    </span>
  )
}) => {
  // Calculate totals for summary display
  const subtotal = calculateSubtotal(items);
  const refInvoiceAmount = parseFloat(referenceInvoice.amount || 0);
  const additionalCostValue = parseFloat(additionalCost || 0);
  const discountAmountValue = parseFloat(discountAmount || 0);

  // Calculate percentages against reference invoice amount
  const additionalCostPercentage = refInvoiceAmount ? ((additionalCostValue / refInvoiceAmount) * 100).toFixed(2) : 0;
  const discountPercentageValue = refInvoiceAmount ? ((discountAmountValue / refInvoiceAmount) * 100).toFixed(2) : 0;
  
  const {t} = useTranslation();

  console.log('Credit Note Summary Calculations:', {
    referenceInvoiceAmount: refInvoiceAmount,
    subtotal,
    additionalCost: {
      value: additionalCostValue,
      percentage: additionalCostPercentage
    },
    discount: {
      value: discountAmountValue,
      percentage: discountPercentageValue,
      userSpecifiedPercentage: discountPercentage
    }
  });

  const netAmount = subtotal + additionalCostValue - discountAmountValue;
  const vatAmount = calculateVat(items);
  const grandTotal = calculateTotal(items);
  const totalPaid = getTotalPayments ? getTotalPayments() : 0;
  const remainingBalance = getRemainingBalance ? getRemainingBalance() : grandTotal;

  // Get payment status label and color
  const getPaymentStatus = () => {
    if (totalPaid === 0) return { label: 'Not Paid', color: 'error' };
    if (remainingBalance > 0) return { label: 'Partially Paid', color: 'warning' };
    if (totalPaid >= grandTotal) return { label: 'Paid', color: 'success' };
    return { label: 'Unknown', color: 'default' };
  };

  const paymentStatus = getPaymentStatus();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#f8faff',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalculateIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {t("Common.Summary")}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Collapse in={true} timeout="auto">
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px solid rgba(0, 0, 0, 0.09)'
            }}
          >
            <Stack spacing={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                 {t("Common.Subtotal")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(subtotal)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  {t('CreditNotes.Additional_Cost')}
                  {additionalCostValue > 0 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                      ({additionalCostPercentage}% of {formatCurrency(refInvoiceAmount)})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(additionalCostValue)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                {t('Common.Discount')}
                  {discountAmountValue > 0 && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                      ({discountPercentageValue}% of {formatCurrency(refInvoiceAmount)})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: discountAmountValue > 0 ? 'error.main' : 'text.primary', display: 'flex', alignItems: 'center' }}>
                  {discountAmountValue > 0 ? '- ' : ''}{formatCurrency(discountAmountValue)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)', backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  {t('Common.Net_Amount')}
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                    (Subtotal + Additional - Discount)
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(netAmount)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  {t('Common.VAT')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(vatAmount)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#e8f4ff',
                  p: 1.5,
                  borderRadius: '4px',
                  border: '1px solid rgba(33, 150, 243, 0.2)'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', flexGrow: 1 }}>
                  {t('Common.Total_Amount')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(grandTotal)}
                </Typography>
              </Box>

              {/* Payment Information */}
              {totalPaid > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                      Total Paid
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                      {formatCurrency(totalPaid)}
                    </Typography>
                  </Box>

                  {remainingBalance !== 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 1,
                        p: 1.5,
                        backgroundColor: remainingBalance > 0 ? '#fff8e1' : '#e8f5e9',
                        borderRadius: '4px',
                        border: remainingBalance > 0
                          ? '1px dashed #ffd54f'
                          : '1px dashed #a5d6a7'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                        {remainingBalance > 0 ? 'Balance Due' : 'Change Amount'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: remainingBalance > 0 ? 'warning.dark' : 'success.dark'
                        }}
                      >
                        {formatCurrency(Math.abs(remainingBalance))}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CreditNoteSummary;